/**
 * Reward tiers inside Recharge's cart drawer.
 *
 * The drawer customers see is Recharge Storefront Experiences', not the theme's - it lives in an
 * OPEN shadow root under div#recharge-storefront-experiences__cart-drawer. Recharge's own progress
 * bar supports a single threshold (shipping_progress_bar.required_amount), so a second reward tier
 * cannot be configured in their admin.
 *
 * Rather than rebuild the drawer, this drives Recharge's own elements: their fill, their icon
 * bubble, their message richtext, their carousel. They keep their styling tokens and 450ms
 * transitions; only the numbers, copy, icon and the chips are ours. Their markup is addressed
 * through the stable hooks they expose - [data-slot="progress"], [data-slot="carousel-item"],
 * [part="money"] - rather than their Tailwind classes, which are the parts most likely to churn.
 *
 * Recharge re-renders the drawer on every cart change and wipes anything we added, so every pass
 * is idempotent and re-runs from a MutationObserver. Our own writes would retrigger that observer,
 * hence the `applying` guard and the write-only-if-changed checks.
 *
 * That observer watches characterData as well as childList. Recharge rewrites the progress copy by
 * updating the text node in place rather than replacing the element, which is not a childList
 * mutation - without it their "Congrats" wording silently won back the message every time a tier
 * was earned, and only that tier.
 */
(function () {
  "use strict";

  var configEl = document.getElementById("gigli-cart-rewards-config");
  if (!configEl) return;

  var CFG;
  try {
    CFG = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  var HOST_ID = "recharge-storefront-experiences__cart-drawer";
  var CHIP_ATTR = "data-gigli-chip";
  var GIFT_ATTR = "data-gigli-gift";
  var DISC_ATTR = "data-gigli-discount";
  var CARD_ATTR = "data-gigli-card";
  var HIDDEN_ATTR = "data-gigli-hidden";
  var STYLE_ATTR = "data-gigli-rewards-style";

  /* Recharge's icon bubble is 32px wide and positioned by its left edge, so every position along
     the track is offset by half of that to centre it - the same maths their own code uses. */
  var BUBBLE_HALF = 16;

  var GIFTS = (CFG.gifts || []).filter(function (g) {
    return g && g.productId && g.cents > 0;
  });

  var state = { basis: 0, inCart: {}, ready: false };
  var applying = false;
  var queued = null;
  var fetching = false;
  var warned = false;
  var refreshTimer = null;
  var refetch = false;

  /* ------------------------------------------------------------------ helpers */

  function money(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === "function") {
        return Shopify.formatMoney(cents, CFG.moneyFormat);
      }
    } catch (e) {}
    return "$" + (cents / 100).toFixed(2);
  }

  function root() {
    var host = document.getElementById(HOST_ID);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function cartUrl(path) {
    var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    return base + path;
  }

  /* Same basis the theme's Liquid uses on /cart: shippable line items less cart-level discounts.
     Recharge's own bar is configured "discounted", so the two agree. */
  function readCart() {
    return fetch(cartUrl("cart.js"), {
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var sum = 0;
        (cart.items || []).forEach(function (i) {
          if (i.requires_shipping) sum += i.final_line_price;
        });
        (cart.cart_level_discount_applications || []).forEach(function (d) {
          sum -= d.total_allocated_amount;
        });
        state.basis = sum;
        state.inCart = {};
        GIFTS.forEach(function (g) {
          state.inCart[g.productId] = (cart.items || []).some(function (i) {
            return i.product_id === g.productId;
          });
        });
        state.ready = true;
        return cart;
      })
      .catch(function () {});
  }

  /** Mirrors snippets/cart-rewards-bar.liquid exactly, so the drawer and /cart never disagree. */
  function compute() {
    var rewards = [];
    if (CFG.shipping && CFG.shipping.enabled) {
      rewards.push({
        kind: "shipping",
        cents: CFG.shipping.cents,
        label: CFG.shipping.label,
      });
    }
    GIFTS.forEach(function (g) {
      rewards.push({ kind: "gift", cents: g.cents, label: g.title, gift: g });
    });
    if (!rewards.length) return null;

    var max = 0;
    rewards.forEach(function (r) {
      if (r.cents > max) max = r.cents;
    });

    var next = null;
    rewards.forEach(function (r) {
      if (state.basis < r.cents && (!next || r.cents < next.cents)) next = r;
    });

    var pct = max > 0 ? Math.min(100, (state.basis / max) * 100) : 0;

    return { rewards: rewards, max: max, next: next, pct: pct };
  }

  function svgWrap(inner) {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 28" fill="none" aria-hidden="true" ' +
      'style="width:100%;height:100%;display:block">' +
      inner +
      "</svg>"
    );
  }

  function giftBadgeSvg() {
    return svgWrap(
      '<circle cx="13.3" cy="10.6" r="1.75" fill="currentColor"/>' +
        '<circle cx="15.9" cy="10.6" r="1.75" fill="currentColor"/>' +
        '<rect x="9.3" y="11.7" width="10.6" height="2.5" rx="0.6" fill="currentColor"/>' +
        '<rect x="10.4" y="14.5" width="8.4" height="5" rx="0.6" fill="currentColor"/>'
    );
  }

  /* Same truck artwork the theme's own bar uses, recoloured with currentColor so the chip's
     reached/unreached states drive it. */
  function truckBadgeSvg() {
    return svgWrap(
      '<path fill="currentColor" d="M7.4678 9.41309C7.1779 9.41309 6.9375 9.64227 6.9375 9.91864C6.9375 10.195 7.1779 10.4242 7.4678 10.4242H12.0638C12.3537 10.4242 12.5941 10.6534 12.5941 10.9298C12.5941 11.2061 12.3537 11.4353 12.0638 11.4353H7.4678C7.1779 11.4353 6.9375 11.6645 6.9375 11.9409C6.9375 12.2172 7.1779 12.4464 7.4678 12.4464H10.6496C10.9395 12.4464 11.1799 12.6756 11.1799 12.952C11.1799 13.2283 10.9395 13.4575 10.6496 13.4575H7.4678C7.1779 13.4575 6.9375 13.6867 6.9375 13.9631C6.9375 14.2395 7.1779 14.4686 7.4678 14.4686H9.23548C9.52538 14.4686 9.76578 14.6978 9.76578 14.9742C9.76578 15.2506 9.52538 15.4798 9.23548 15.4798H7.4678C7.1779 15.4798 6.9375 15.7089 6.9375 15.9853V16.6594C6.9375 17.4952 7.65164 18.176 8.52841 18.176H8.78295C9.01629 18.9512 9.76578 19.5242 10.6496 19.5242C11.5335 19.5242 12.29 18.9512 12.5163 18.176H15.7759V10.9298C15.7759 10.0939 15.0617 9.41309 14.185 9.41309H7.4678ZM16.8365 10.7612V17.6705C16.8365 18.6883 17.7133 19.5242 18.7809 19.5242C19.6648 19.5242 20.4213 18.9512 20.6476 18.176H20.9021C21.786 18.176 22.4931 17.502 22.4931 16.6594V14.5158C22.4931 14.3069 22.4436 14.0912 22.3516 13.8957L21.3122 11.6578C21.0577 11.1118 20.492 10.7612 19.8628 10.7612H16.8365ZM18.2506 11.7723H19.8628C20.0749 11.7723 20.2587 11.8869 20.3436 12.0757L20.9234 13.3227C21.0294 13.5452 20.8597 13.7946 20.5981 13.7946H18.2436C18.0456 13.7946 17.89 13.6463 17.89 13.4575V12.1094C17.89 11.9206 18.0456 11.7723 18.2436 11.7723H18.2506ZM10.6496 16.8279C11.1375 16.8279 11.5335 17.2054 11.5335 17.6705C11.5335 18.1356 11.1375 18.5131 10.6496 18.5131C10.1617 18.5131 9.76578 18.1356 9.76578 17.6705C9.76578 17.2054 10.1617 16.8279 10.6496 16.8279ZM18.7809 16.8279C19.2688 16.8279 19.6648 17.2054 19.6648 17.6705C19.6648 18.1356 19.2688 18.5131 18.7809 18.5131C18.2931 18.5131 17.8971 18.1356 17.8971 17.6705C17.8971 17.2054 18.2931 16.8279 18.7809 16.8279Z"/>'
    );
  }

  function badgeSvg(kind) {
    return kind === "gift" ? giftBadgeSvg() : truckBadgeSvg();
  }

  /* ------------------------------------------------------------------ styles */

  var CSS =
    "[" +
    CHIP_ATTR +
    "]{position:absolute;top:calc(50% + 8px);transform:translate(-50%,-50%);" +
    "width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;" +
    "background:#fff;box-shadow:0 0 0 2px currentColor;color:#dd1254;z-index:3;pointer-events:none;" +
    "opacity:.45;filter:grayscale(1);transition:opacity .3s ease,filter .3s ease}" +
    "[" +
    CHIP_ATTR +
    '][data-reached="true"]{opacity:1;filter:none}' +
    "[" +
    CHIP_ATTR +
    "] svg{width:12px;height:12px}" +
    /* A box card gets the branded surface and the pink button wherever it renders. The drawer
       stylesheet only reaches cards inside a carousel item ([data-slot=carousel-item]) or under
       a [role=dialog] that is not #CartSheet - neither covers the plain single-card shape, which
       is why one came out grey with their default dark button. Their inline grey background is
       beaten here because an !important declaration outranks an inline one. The tokens are
       Recharge's own, resolved from the same shadow root. */
    "[" +
    CARD_ATTR +
    "]{background:var(--gigli-sfe-surface,#fff)!important;" +
    "border:1px solid var(--gigli-sfe-pink-soft,#f99fc9)!important;" +
    "border-radius:var(--gigli-sfe-radius-chip,12px)!important}" +
    "[" +
    CARD_ATTR +
    '] button[data-slot="button"]{' +
    "background-color:var(--gigli-sfe-pink,#dd1254)!important;" +
    "color:var(--gigli-sfe-surface,#fff)!important;" +
    "border-radius:var(--gigli-sfe-radius-pill,999px)!important}";

  function injectStyles(sr) {
    try {
      if (sr.adoptedStyleSheets && typeof CSSStyleSheet === "function") {
        var already = sr.adoptedStyleSheets.some(function (s) {
          return s.__gigliRewards;
        });
        if (already) return;
        var sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
        sheet.__gigliRewards = true;
        sr.adoptedStyleSheets = sr.adoptedStyleSheets.concat(sheet);
        return;
      }
    } catch (e) {}
    if (sr.querySelector("style[" + STYLE_ATTR + "]")) return;
    var tag = document.createElement("style");
    tag.setAttribute(STYLE_ATTR, "");
    tag.textContent = CSS;
    sr.appendChild(tag);
  }

  /* ------------------------------------------------------------------ the bar */

  function applyBar(sr, s) {
    var progress = sr.querySelector('[data-slot="progress"]');
    if (!progress) return;

    var track = progress.parentElement;
    var fill = progress.firstElementChild;
    var pct = s.pct;

    /* Recharge scales its fill to its own single threshold. Ours has to span the furthest reward,
       so the fill, the aria values and the icon bubble are all re-driven off our own maximum. */
    if (fill) {
      var want = "translateX(-" + (100 - pct).toFixed(4) + "%)";
      if (fill.style.transform !== want) fill.style.transform = want;
    }
    progress.setAttribute("data-value", String(pct));
    progress.setAttribute("aria-valuenow", String(pct));
    progress.setAttribute("aria-valuetext", Math.round(pct) + "%");

    var bubble = null;
    for (var i = 0; i < track.children.length; i++) {
      if (
        track.children[i] !== progress &&
        track.children[i].querySelector("img")
      ) {
        bubble = track.children[i];
        break;
      }
    }
    if (bubble) {
      var left = "calc(" + pct.toFixed(4) + "% - " + BUBBLE_HALF + "px)";
      if (bubble.style.left !== left) bubble.style.left = left;

      /* The truck is a merchant image uploaded in the Recharge admin; when the next reward is the
         gift we replace it with an inline SVG sibling and hide theirs, rather than swapping the
         src to something they would overwrite on re-render. */
      var img = bubble.querySelector("img");
      var svg = bubble.querySelector("[" + GIFT_ATTR + "]");
      var wantGift = s.next ? s.next.kind === "gift" : hasGiftAsFurthest(s);

      if (wantGift) {
        if (!svg) {
          svg = document.createElement("span");
          svg.setAttribute(GIFT_ATTR, "");
          svg.style.cssText = "width:60%;height:60%;color:#fff;display:block";
          svg.innerHTML = giftBadgeSvg();
          bubble.appendChild(svg);
        }
        if (img && img.style.display !== "none") img.style.display = "none";
      } else {
        if (svg) svg.remove();
        if (img && img.style.display === "none") img.style.display = "";
      }
    }

    applyChips(track, s);
  }

  function hasGiftAsFurthest(s) {
    var furthest = null;
    s.rewards.forEach(function (r) {
      if (!furthest || r.cents > furthest.cents) furthest = r;
    });
    return furthest && furthest.kind === "gift";
  }

  function applyChips(track, s) {
    if (getComputedStyle(track).position === "static")
      track.style.position = "relative";

    var seen = {};
    s.rewards.forEach(function (r) {
      /* Keyed by kind AND value: with two gift tiers the kind alone is not unique, and reusing
         one node for both would leave a chip stranded at the wrong threshold. */
      var key = r.kind + ":" + r.cents;
      seen[key] = true;
      var chip = track.querySelector("[" + CHIP_ATTR + '="' + key + '"]');
      if (!chip) {
        chip = document.createElement("span");
        chip.setAttribute(CHIP_ATTR, key);
        chip.innerHTML = badgeSvg(r.kind);
        track.appendChild(chip);
      }
      var pos = s.max > 0 ? (r.cents / s.max) * 100 : 0;
      var left = pos.toFixed(2) + "%";
      if (chip.style.left !== left) chip.style.left = left;
      var reached = state.basis >= r.cents ? "true" : "false";
      if (chip.getAttribute("data-reached") !== reached)
        chip.setAttribute("data-reached", reached);
      chip.setAttribute("title", r.label);
    });

    /* Drop chips for rewards that no longer exist - a tier switched off in the customiser. */
    Array.prototype.forEach.call(
      track.querySelectorAll("[" + CHIP_ATTR + "]"),
      function (c) {
        if (!seen[c.getAttribute(CHIP_ATTR)]) c.remove();
      }
    );
  }

  /* ------------------------------------------------------------------ the message */

  var MSG_ATTR = "data-gigli-msg";

  /* We do not write into Recharge's own <p>. Both sides re-render on cart change, and editing
     their node turned into a write-war: whichever of us wrote last won, so the drawer showed
     their "Congrats" copy or ours depending on timing. Instead theirs is hidden and ours sits
     beside it, cloned from theirs so it inherits their richtext styling. They never touch a node
     they did not create; if a re-render drops it, the observer puts it back. */
  function applyMessage(sr, s) {
    var progress = sr.querySelector('[data-slot="progress"]');
    if (!progress) return;
    var block =
      progress.closest("div.px-drawer") || progress.parentElement.parentElement;
    if (!block) return;

    var theirs = null;
    var ps = block.querySelectorAll("p");
    for (var i = 0; i < ps.length; i++) {
      if (!ps[i].hasAttribute(MSG_ATTR)) {
        theirs = ps[i];
        break;
      }
    }

    var html;
    if (s.next) {
      var amount =
        '<strong><span part="money">' +
        money(s.next.cents - state.basis) +
        "</span></strong>";
      html = CFG.strings.nextReward
        .replace("__AMOUNT__", amount)
        .replace("__REWARD__", escapeHtml(s.next.label));
    } else {
      html = escapeHtml(CFG.strings.allUnlocked);
    }

    var ours = block.querySelector("p[" + MSG_ATTR + "]");
    if (!ours) {
      ours = theirs ? theirs.cloneNode(false) : document.createElement("p");
      ours.setAttribute(MSG_ATTR, "");
      if (theirs && theirs.parentNode)
        theirs.parentNode.insertBefore(ours, theirs.nextSibling);
      else block.insertBefore(ours, block.firstChild);
    }
    if (ours.innerHTML !== html) ours.innerHTML = html;
    if (theirs && theirs.style.display !== "none")
      theirs.style.display = "none";
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  /* ------------------------------------------------------------------ gifts in "Goes great with" */

  var HEAD_ATTR = "data-gigli-orig-heading";

  /**
   * The gift on offer is the one for the tier the cart has actually REACHED - the highest
   * threshold met - not every tier passed on the way up. Crossing into a higher tier swaps the
   * product, and dropping back below it swaps back to the tier below, because this is recomputed
   * from the live basis on every pass rather than accumulated.
   *
   * If that tier's gift is already in the cart there is nothing left to offer, so the carousel
   * goes back to Recharge's own products - it does not fall through to a lower tier the customer
   * has already climbed past.
   */
  function unlockedGifts() {
    var earned = GIFTS.filter(function (g) {
      return g.available && state.basis >= g.cents;
    }).sort(function (a, b) {
      return b.cents - a.cents;
    });
    var tier = earned[0];
    if (!tier || state.inCart[tier.productId]) return [];
    return [tier];
  }

  /* The slide the gift card is cloned from.

     Recharge's own slides are not interchangeable. Which products they recommend changes as the
     cart changes, and a card for a product ALREADY IN THE CART renders a different shape - a
     quantity stepper in place of the add button, and its own price row - so cloning "the first
     slide" produced a different card depending on when the takeover happened. That is what turned
     up once a mystery box was in the cart: a stepper button (styled by the drawer's +/- rules
     rather than the pink primary one) and a second price tag inherited from the template.

     So the first PLAIN slide seen is kept, detached, and reused for the rest of the session. */
  var slideTemplate = null;

  function pickTemplate(slides) {
    for (var i = 0; i < slides.length; i++) {
      var candidate = slides[i];
      if (candidate.hasAttribute(GIFT_ATTR + "-item")) continue;
      /* Their own icon classes - the theme's drawer stylesheet keys off these too - mark a card
         whose product is in the cart. That card has no add button to give us. */
      if (candidate.querySelector(".lucide-circle-minus, .lucide-circle-plus"))
        continue;
      slideTemplate = candidate.cloneNode(true);
      slideTemplate.style.display = "";
      return slideTemplate;
    }
    /* Nothing plain on offer right now: reuse the one kept from an earlier render. */
    return slideTemplate;
  }

  /* The look a mystery-box card gets, wherever it comes from.

     The drawer stylesheet (recharge-storefront-experiences.min.css) paints the card surface off
     Recharge's own rounded token and the pink button off their primary one - and their cards do
     not all carry those classes, which is why a box advertised by their own carousel came out
     grey with a dark button while ours came out branded. */
  function styleCard(slide) {
    /* The card is the slide itself in the plain shape, and a descendant of it inside a carousel
       item. The image carries the same rounded class, hence div. */
    var card = slide.classList.contains("rounded-storefront-drawer")
      ? slide
      : slide.querySelector("div.rounded-storefront-drawer") ||
        slide.firstElementChild;
    if (card && card.tagName !== "IMG") {
      if (!card.hasAttribute(CARD_ATTR)) card.setAttribute(CARD_ATTR, "");
      if (!card.classList.contains("rounded-storefront-drawer")) {
        card.classList.add("rounded-storefront-drawer");
      }
    }

    var btn = slide.querySelector('button[data-slot="button"], button');
    if (btn && !btn.classList.contains("bg-storefront-drawer-primary")) {
      btn.classList.add("bg-storefront-drawer-primary");
    }
    /* A slide cloned from one of theirs inherits whatever inline display it carried, including a
       "none" left by an older pass of this script. */
    if (btn && btn.style.display === "none") btn.style.display = "";

    /* One price. Some of their cards carry a second money node - a compare-at, or the price
       beside a quantity - and two figures on one card read as a mistake. */
    var monies = slide.querySelectorAll('[part="money"]');
    for (var m = 1; m < monies.length; m++) {
      if (monies[m].style.display !== "none") monies[m].style.display = "none";
    }
    return monies[0] || null;
  }

  /* The card's title: the first element that holds text and nothing else. On their markup that
     is div.text-storefront-drawer-foreground.font-semibold, but it is found by shape rather than
     by class, because the classes are theirs to change. */
  function titleNode(slide) {
    var divs = slide.querySelectorAll("div");
    for (var i = 0; i < divs.length; i++) {
      if (divs[i].children.length === 0 && divs[i].textContent.trim()) return divs[i];
    }
    return null;
  }

  /* The image and the name open the product page. Their card is built entirely out of divs -
     there is no anchor anywhere in it - so the behaviour is added rather than borrowed. A real
     <a> would mean restructuring markup React owns on their own cards, so these get a link role
     and keyboard handling instead of an element swap. */
  function linkToProduct(slide, g) {
    if (!g || !g.url) return;
    var targets = [];
    var img = slide.querySelector("img");
    if (img) targets.push(img);
    var title = titleNode(slide);
    if (title) targets.push(title);

    targets.forEach(function (el) {
      if (el.__gigliLinked) return;
      el.__gigliLinked = true;
      el.style.cursor = "pointer";
      el.setAttribute("role", "link");
      el.setAttribute("tabindex", "0");
      var go = function (ev) {
        ev.preventDefault();
        /* Their card may carry handlers of its own; this click is only ever about the product. */
        ev.stopPropagation();
        window.location.href = g.url;
      };
      el.addEventListener("click", go);
      el.addEventListener("keydown", function (ev) {
        if (ev.key === "Enter" || ev.key === " " || ev.key === "Spacebar") go(ev);
      });
    });
  }

  /* Which gift, if any, a slide of theirs is advertising.

     They expose no product id in the markup, so this goes on two weaker signals and accepts
     either. The title comes first, compared with whitespace collapsed and case folded - exact
     equality missed cards whose title is wrapped, padded or split across nodes. The image is the
     fallback: Shopify serves the same file at whatever size is asked for, so the filename
     survives while the query string does not, but two tiers may share one file. */
  function normalizeText(value) {
    return String(value == null ? "" : value)
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function imageKey(url) {
    if (!url) return "";
    var path = String(url).split("?")[0];
    var slash = path.lastIndexOf("/");
    return slash < 0 ? path : path.slice(slash + 1);
  }

  function giftForSlide(slide) {
    var text = normalizeText(slide.textContent);
    var i;
    for (i = 0; i < GIFTS.length; i++) {
      var title = normalizeText(GIFTS[i].title);
      if (title && text.indexOf(title) !== -1) return GIFTS[i];
    }

    /* Two tiers can share one image file, so this only runs when the title said nothing. */
    var img = slide.querySelector("img");
    var key = imageKey(img && img.getAttribute("src"));
    if (key) {
      for (i = 0; i < GIFTS.length; i++) {
        if (imageKey(GIFTS[i].image) === key) return GIFTS[i];
      }
    }
    return null;
  }

  /* The cross-sell block, in EITHER shape Recharge renders it.

     With several recommendations they build a carousel; with one they drop the carousel entirely
     and emit just the heading and a card:

       div.px-drawer > div > div.dynamic-richtext > h4      "Goes great with"
                          > div.rounded-storefront-drawer   the card

     Keying off [data-slot="carousel-content"] alone meant everything here - the gift takeover and
     the restyling both - silently did nothing whenever the drawer was in that second shape, which
     is what it falls into once a box is in the cart and only one recommendation is left. */
  function findCrossSell(sr) {
    var content = sr.querySelector('[data-slot="carousel-content"] > div');
    if (content) {
      var carousel = sr.querySelector('[data-slot="carousel"]');
      return {
        list: content,
        carousel: carousel,
        block:
          (carousel &&
            (carousel.closest("div.px-drawer") || carousel.parentElement)) ||
          content.parentElement
      };
    }

    var cards = sr.querySelectorAll("div.rounded-storefront-drawer");
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i].querySelector('button[data-slot="button"]')) continue;
      var list = cards[i].parentElement;
      /* The heading is what separates the cross-sell block from a cart line item, which is
         built out of the same card class. */
      if (!list || !list.querySelector("h4")) continue;
      return {
        list: list,
        carousel: null,
        block: cards[i].closest("div.px-drawer") || list
      };
    }
    return null;
  }

  /* The cards in the block. In the plain shape the heading is a sibling of them, so it has to be
     stepped over - hiding it along with the cards took the "Goes great with" title with it. */
  function slidesIn(list) {
    var out = [];
    for (var i = 0; i < list.children.length; i++) {
      var child = list.children[i];
      if (child.tagName === "H4") continue;
      if (child.querySelector && child.querySelector("h4")) continue;
      out.push(child);
    }
    return out;
  }

  /* Put Recharge's own upsell back exactly as it was.

     This works off the whole shadow root rather than the block we happen to have found. Once
     every gift is in the cart there is nothing of ours left to advertise, and their upsell has to
     return even if the drawer has since re-rendered into its other shape - our card and the cards
     we hid would then be sitting in a container findCrossSell no longer points at, and would stay
     hidden for as long as the drawer was open. Hidden nodes are marked rather than inferred, so
     nothing Recharge hid for its own reasons is ever forced back on. */
  function restoreTheirs(sr, ctx) {
    Array.prototype.forEach.call(
      sr.querySelectorAll("[" + GIFT_ATTR + "-item]"),
      function (n) {
        n.remove();
      }
    );
    Array.prototype.forEach.call(
      sr.querySelectorAll("[" + HIDDEN_ATTR + "]"),
      function (n) {
        n.removeAttribute(HIDDEN_ATTR);
        if (n.style.display === "none") n.style.display = "";
      }
    );
    var ourHead = sr.querySelector("h4[" + HEAD_ATTR + "]");
    if (ourHead) ourHead.remove();

    if (!ctx) return;

    /* A heading hidden by an earlier build of this file carries no marker, and a drawer that is
       open across the deploy would keep "Goes great with" missing until Recharge next rebuilds
       the block. Theirs is the h4 without our attribute. */
    if (ctx.block) {
      Array.prototype.forEach.call(
        ctx.block.querySelectorAll("h4"),
        function (h) {
          if (h.hasAttribute(HEAD_ATTR)) return;
          if (h.style.display === "none") h.style.display = "";
        }
      );
    }

    slidesIn(ctx.list).forEach(function (c) {
      /* Their card for a box, at their price - that tier is either not unlocked or already
         claimed, so nothing here touches the figure. Only the styling and the link are brought
         into line with ours. */
      var gift = giftForSlide(c);
      if (!gift) return;
      styleCard(c);
      linkToProduct(c, gift);
    });
    setArrows(ctx.carousel, true);
  }

  function applyGifts(sr) {
    var ctx = findCrossSell(sr);
    var gifts = unlockedGifts();

    if (!gifts.length) {
      restoreTheirs(sr, ctx);
      return;
    }
    if (!ctx) return;

    var list = ctx.list;
    var carousel = ctx.carousel;
    var ggBlock = ctx.block;

    var heading = null;
    if (ggBlock) {
      var h4s = ggBlock.querySelectorAll("h4");
      for (var k = 0; k < h4s.length; k++) {
        if (!h4s[k].hasAttribute(HEAD_ATTR)) {
          heading = h4s[k];
          break;
        }
      }
    }

    var mine = list.querySelectorAll("[" + GIFT_ATTR + "-item]");
    var slides = slidesIn(list);

    var template = pickTemplate(slides);
    if (!template) return;

    /* Drop slides for a tier that is no longer the achieved one. Without this, climbing from one
       tier to the next would leave the previous gift sitting alongside the new one - the old slide
       still carries our attribute, so the visibility pass below would keep showing it. */
    var wanted = {};
    gifts.forEach(function (g) {
      wanted[g.productId] = true;
    });
    Array.prototype.forEach.call(mine, function (n) {
      if (!wanted[n.getAttribute(GIFT_ATTR + "-item")]) n.remove();
    });

    gifts.forEach(function (g) {
      var slide = list.querySelector(
        "[" + GIFT_ATTR + '-item="' + g.productId + '"]'
      );
      if (!slide) {
        /* Clone one of Recharge's own cards so the gift inherits their markup verbatim. Building
           it by hand would mean copying thirty utility classes that are theirs to change. */
        slide = template.cloneNode(true);
        slide.setAttribute(GIFT_ATTR + "-item", String(g.productId));
        /* Before their first card, never before the heading. */
        list.insertBefore(slide, slides[0] || null);
      }
      fillSlide(slide, g);
    });

    /* Only the unlocked gifts show while they are on offer. Each card we hide is marked, so the
       restore knows which ones are ours to bring back. */
    slidesIn(list).forEach(function (c) {
      if (c.hasAttribute(GIFT_ATTR + "-item")) {
        if (c.style.display !== "") c.style.display = "";
        return;
      }
      if (!c.hasAttribute(HIDDEN_ATTR)) c.setAttribute(HIDDEN_ATTR, "");
      if (c.style.display !== "none") c.style.display = "none";
    });

    if (heading) {
      /* Same reason as the message: hide theirs, show ours, rather than trading writes. */
      var ourHead = ggBlock.querySelector("h4[" + HEAD_ATTR + "]");
      if (!ourHead) {
        ourHead = heading.cloneNode(false);
        ourHead.setAttribute(HEAD_ATTR, "");
        heading.parentNode.insertBefore(ourHead, heading.nextSibling);
      }
      if (ourHead.textContent !== CFG.strings.unlockedHeading) {
        ourHead.textContent = CFG.strings.unlockedHeading;
      }
      if (!heading.hasAttribute(HIDDEN_ATTR)) heading.setAttribute(HIDDEN_ATTR, "");
      if (heading.style.display !== "none") heading.style.display = "none";
    }
    /* Nothing to page through once the list is just the gifts. */
    setArrows(carousel, false);
  }

  /* Paging arrows only. The slides themselves live in [data-slot="carousel-content"] and carry
     Recharge's own add-to-cart button - hiding every button under the carousel took that with it,
     which left the gift card with nothing to add it by. */
  function setArrows(carousel, show) {
    if (!carousel) return;
    var content = carousel.querySelector('[data-slot="carousel-content"]');
    Array.prototype.forEach.call(
      carousel.querySelectorAll("button"),
      function (b) {
        if (content && content.contains(b)) return;
        var want = show ? "" : "none";
        if (b.style.display !== want) b.style.display = want;
      }
    );
  }

  function fillSlide(slide, g) {
    var img = slide.querySelector("img");
    if (img && g.image && img.getAttribute("src") !== g.image) {
      img.setAttribute("src", g.image);
      img.setAttribute("alt", g.title);
      img.removeAttribute("srcset");
    }
    var title = titleNode(slide);
    if (title && title.textContent.trim() !== g.title) title.textContent = g.title;
    linkToProduct(slide, g);

    /* Price row.
       -------------------------------------------------------------------------------------
       Their money node carries what the customer actually PAYS, so it keeps their price
       styling. The original sits beside it in our own span - smaller, muted, struck through.

       The original price is never written onto one of their elements. text-decoration
       propagates into everything inside an element and cannot be cancelled by a descendant, so
       striking the muted div - which wraps the title as well as the price - struck the product
       name too. Their muted span is not a separate slot for the discount either: it IS the
       price node, so writing the discounted figure into it simply replaced the price and left
       the card with one number instead of two. */
    var priceEl = styleCard(slide);
    var payable = g.discountedFormatted || g.priceFormatted;
    if (priceEl && priceEl.textContent !== payable)
      priceEl.textContent = payable;

    /* Their card prints a variant line under the title - "$50", "10 mg". It belongs to whichever
       product the template was cloned from, and a gift card has no use for it either way. This
       also heals a strike an earlier pass may have left on one of their elements. */
    var mutedDivs = slide.querySelectorAll("div.text-storefront-drawer-muted");
    for (var d = 0; d < mutedDivs.length; d++) {
      var muted = mutedDivs[d];
      if (muted.style.textDecoration) muted.style.textDecoration = "";
      if (muted.hasAttribute(DISC_ATTR) || muted.querySelector('[part="money"]')) continue;
      if (muted.style.display !== "none") muted.style.display = "none";
    }

    var was = slide.querySelector("[" + DISC_ATTR + "]");
    if (g.discountedFormatted && priceEl && priceEl.parentNode) {
      if (!was) {
        was = document.createElement("span");
        was.setAttribute(DISC_ATTR, "");
        was.className = "text-storefront-drawer-muted";
        was.style.cssText =
          "font-size:.85em;opacity:.6;text-decoration:line-through";
        priceEl.parentNode.insertBefore(was, priceEl.nextSibling);
      }
      if (was.textContent !== g.priceFormatted)
        was.textContent = g.priceFormatted;
    } else if (was) {
      was.remove();
    }

    var btn = slide.querySelector('button[data-slot="button"], button');
    if (btn && !btn.__gigliBound) {
      btn.__gigliBound = true;
      btn.addEventListener("click", function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        btn.setAttribute("aria-busy", "true");
        fetch(cartUrl("cart/add.js"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: g.variantId, quantity: 1 }] }),
        }).then(function () {
          btn.setAttribute("aria-busy", "false");
          try {
            if (window.RechargePubSub && window.RechargePubSub.publish) {
              window.RechargePubSub.publish("cart:updated", {});
            }
          } catch (e) {}
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", { bubbles: true })
          );
          refresh();
        });
      });
    }
  }

  /* ------------------------------------------------------------------ orchestration */

  function apply() {
    var sr = root();
    if (!sr || !state.ready) return;
    var s = compute();
    if (!s) return;

    applying = true;
    try {
      injectStyles(sr);
      applyBar(sr, s);
      applyMessage(sr, s);
      applyGifts(sr);
    } catch (e) {
      /* Reported once. This layer depends on another app's DOM, so when a Recharge release moves
         something the failure should be visible in the console rather than silently degrading to
         their default bar. */
      if (!warned) {
        warned = true;
        console.warn("[gigli] cart rewards could not apply:", e && e.message);
      }
    } finally {
      applying = false;
      /* Drop the records our own writes just queued. The `applying` flag cannot do this on its
         own: MutationObserver delivers asynchronously, so by the time the callback runs the flag
         is already false again and every pass we make schedules another one. Anything queued
         during this synchronous block was ours - nobody else can run in the middle of it. */
      if (observer) observer.takeRecords();
    }
  }

  /* Re-paint from cached state on the very next frame. Recharge re-renders the drawer
     periodically and takes our nodes with it, so the restore has to be immediate - going through
     a cart fetch first left their "Congrats" copy on screen for a few hundred milliseconds each
     time, which read as a flicker. */
  function schedule() {
    if (queued) return;
    queued = requestAnimationFrame(function () {
      queued = null;
      apply();
    });
  }

  /* Numbers are refreshed separately and lazily. Nothing on screen waits for this.

     A pending timer is LEFT ALONE rather than pushed back. Resetting it on every mutation meant a
     continuous stream of them - Recharge re-rendering the drawer, its carousel re-initialising
     around the gift slide we insert - could hold the cart re-read permanently 200ms away. The bar
     and the chips then sat on whatever basis was last read, while Recharge's own subtotal, which
     comes from their state rather than from our fetch, kept moving. This way a burst still
     collapses into one read, but never into none. */
  function refreshSoon() {
    if (refreshTimer) return;
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      refresh();
    }, 200);
  }

  /* Coalesced cart re-read. Recharge's own +/- controls change the cart without firing any theme
     event, so the only reliable signal is their re-render - which means the refresh has to be
     cheap and re-entrant. One request in flight at a time; anything asked for meanwhile collapses
     into a single follow-up. */
  function refresh() {
    if (fetching) {
      refetch = true;
      return;
    }
    fetching = true;
    readCart().then(function () {
      fetching = false;
      schedule();
      if (refetch) {
        refetch = false;
        refresh();
      }
    });
  }

  /* ------------------------------------------------------------------ wiring */

  var observed = null;
  var observer = null;

  function watch() {
    subscribeToRecharge();
    var sr = root();
    if (!sr || observed === sr) return;
    observed = sr;
    /* Re-read the cart, not just re-render from cache: when Recharge changes quantities in the
       drawer the totals move without any theme event, and applying stale figures is what made the
       bar lag behind their own. The icon and fill carry Recharge's 450ms transition, so updating
       the same properties they animate makes it slide rather than jump. */
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () {
      schedule(); /* restore our nodes now */
      refreshSoon(); /* re-read the cart behind it */
    });
    observer.observe(sr, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    refresh();
  }

  /* Their pubsub is the direct signal for a quantity change, but subscribing at load never took:
     this file is deferred in the head and RechargePubSub only exists once their bundle has booted,
     which is after the drawer host appears. So it is attempted every time we look for the host,
     and sticks the first time it is there. */
  var subscribed = false;

  function subscribeToRecharge() {
    if (subscribed) return;
    try {
      if (window.RechargePubSub && window.RechargePubSub.subscribe) {
        window.RechargePubSub.subscribe("cart:updated", refresh);
        subscribed = true;
      }
    } catch (e) {}
  }

  /* The drawer host is created by Recharge after their bundle boots, so wait for it rather than
     assuming it exists. Once found, their own re-renders drive everything else. */
  var hostWatcher = new MutationObserver(function () {
    if (document.getElementById(HOST_ID)) watch();
  });
  hostWatcher.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  watch();

  document.addEventListener("cart:change", refresh);
  document.addEventListener("cart:refresh", refresh);
})();
